import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApiClient, useFetchImmutable } from '@marageti/z4-lib';
import { TimeZoneMap } from '@marageti/z4-sdk/lib/standards';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { FieldValues } from 'react-hook-form';
import { useAuthStore } from '../../../store';
import ConfirmPhoneView from './confirm-phone-view';
import TripRequestConfirmationView from './trip-request-confirmation-view';

const DEFAULT_VALUES = {
  phone: {
    prefix: '',
    number: '',
  },
  timeZone: {
    name: '',
    rawOffsetInMinutes: 0,
  },
};

const TripRequestConfirmationContainer = () => {
  const {
    loggedInTraveler,
    setIsAuthenticated,
    setAuthenticatedTraveler,
  } = useAuthStore();
  const apiClient = useApiClient();

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tripRequestId = params.get('tripRequestId');
  const [showConfirmPhone, setShowConfirmPhone] = useState(false);
  const newUser = params.get('newUser');
  const tripRequestDestination = tripRequestId ? `/trip-requests/${tripRequestId}` : undefined;

  const timezones = useFetchImmutable<TimeZoneMap>(apiClient.standardsClient.getTimeZones, [], true);
  const timezonesMap = timezones.data ? new Map(Object.entries(timezones.data)) : new Map();
  const suggestedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ');
  const suggestedTimezoneOption = timezonesMap.has(suggestedTimezone) ? {
    name: suggestedTimezone,
    rawOffsetInMinutes: timezonesMap.get(suggestedTimezone),
  }
    : DEFAULT_VALUES.timeZone;

  const handleViewClick = () => {
    if (newUser) {
      setShowConfirmPhone(true);
    } else if (tripRequestId) {
      navigate(tripRequestDestination || '/');
    }
  };

  const onPhoneSubmit = async (data: FieldValues) => {
    if (loggedInTraveler) {
      const { phone, timeZone } = data;
      const request = {
        ...loggedInTraveler,
        ...(phone.number && { phone: data.phone }),
        ...(timeZone.name && { timeZone: data.timeZone }),
      };
      try {
        const res: Traveler = await apiClient.peopleClient.putTraveler(loggedInTraveler.id, request);
        setAuthenticatedTraveler(res);
      } catch (error) {
        console.error('Failed to submit phone:', error);
      } finally {
        setIsAuthenticated(true);
        navigate(tripRequestDestination || '/');
      }
    } else {
      setIsAuthenticated(true);
      navigate(tripRequestDestination || '/');
    }
  };

  return (
    <>
      {(showConfirmPhone && newUser) && (
      <ConfirmPhoneView
        defaultValues={{
          phone: loggedInTraveler?.phone || DEFAULT_VALUES.phone,
          timeZone: loggedInTraveler?.timeZone.name ? loggedInTraveler?.timeZone : suggestedTimezoneOption,
        }}
        onSubmit={onPhoneSubmit}
      />
      )}
      {!showConfirmPhone && (
      <TripRequestConfirmationView
        handleViewClick={handleViewClick}
        isViewButtonDisabled={!tripRequestId}
        newUser={!!newUser}
      />
      )}
    </>
  );
};

export default TripRequestConfirmationContainer;
